// Minimal markdown → React renderer for chat bubbles.
//
// We deliberately avoid pulling in a full markdown library to keep the
// bundle small and the rendering surface predictable. The assistant
// produces a small subset — paragraphs, headings, bullets, bold,
// italic, inline code, fenced code, and tables — which this renderer
// covers. After rendering, brand names that match the supplied
// `brandIndex` get swapped for <BrandChip> nodes.

import React from 'react';
import BrandChip from './BrandChip';

const HEADING = /^(#{1,6})\s+(.+)$/;
const FENCE = /^```/;
const TABLE_SEP = /^\s*\|?(\s*:?-+:?\s*\|)+\s*:?-+:?\s*\|?\s*$/;
const BULLET = /^(\s*)([-*])\s+(.+)$/;
const NUMBERED = /^(\s*)\d+\.\s+(.+)$/;

// Inline: bold, italic, inline code. Order matters — handle the longer
// markers first so `**foo**` doesn't degrade into italic.
function renderInline(text, brandIndex, keyPrefix) {
  if (!text) return null;
  const out = [];
  let i = 0;
  let buf = '';
  const flush = () => {
    if (buf) {
      out.push(...renderWithChips(buf, brandIndex, `${keyPrefix}-${out.length}`));
      buf = '';
    }
  };
  while (i < text.length) {
    if (text.startsWith('**', i)) {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        flush();
        out.push(
          <strong key={`${keyPrefix}-b-${out.length}`}>
            {renderInline(text.slice(i + 2, end), brandIndex, `${keyPrefix}-bi`)}
          </strong>,
        );
        i = end + 2;
        continue;
      }
    }
    if (text[i] === '`') {
      const end = text.indexOf('`', i + 1);
      if (end !== -1) {
        flush();
        out.push(
          <code key={`${keyPrefix}-c-${out.length}`} className="chat-inline-code">
            {text.slice(i + 1, end)}
          </code>,
        );
        i = end + 1;
        continue;
      }
    }
    if (text[i] === '*' && text[i + 1] !== '*') {
      const end = text.indexOf('*', i + 1);
      if (end !== -1) {
        flush();
        out.push(
          <em key={`${keyPrefix}-i-${out.length}`}>
            {renderInline(text.slice(i + 1, end), brandIndex, `${keyPrefix}-ii`)}
          </em>,
        );
        i = end + 1;
        continue;
      }
    }
    buf += text[i];
    i += 1;
  }
  flush();
  return out;
}

// Walk a text segment and replace any brand name that appears in
// `brandIndex` (longest-name-first to keep "Liquid Death" from being
// stolen by "Liquid").
function renderWithChips(text, brandIndex, keyPrefix) {
  if (!brandIndex || brandIndex.length === 0) return [text];
  const names = brandIndex.map(b => b.name);
  // Longest first.
  const sorted = [...names].sort((a, b) => b.length - a.length);
  // Escape regex special chars in brand names.
  const escaped = sorted.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`\\b(${escaped.join('|')})\\b`, 'g');
  const segments = [];
  let last = 0;
  let m;
  let count = 0;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) segments.push(text.slice(last, m.index));
    const matched = m[1];
    const brand = brandIndex.find(b => b.name === matched);
    segments.push(
      <BrandChip
        key={`${keyPrefix}-chip-${count++}`}
        id={brand.id}
        name={brand.name}
        hovercard={brand.hovercard}
      />,
    );
    last = m.index + matched.length;
  }
  if (last < text.length) segments.push(text.slice(last));
  return segments.length ? segments : [text];
}

// A cell that is a bare figure — a count, a percentage, a currency
// amount, a "2.1×" multiple. Numbers read wrong left-aligned against
// text: the digits don't line up and you can't scan a column for the
// biggest one, which is the whole point of putting them in a table.
const NUMERIC_CELL = /^[-+]?[$€£¥]?\s?\d[\d,\s]*(\.\d+)?\s?[%×xKMB]?$/i;

function looksNumeric(cell) {
  const text = String(cell || '').trim();
  if (!text || text === '—' || text === '-') return false;
  return NUMERIC_CELL.test(text.replace(/\*\*/g, ''));
}

function parseTable(lines, startIdx) {
  // Header line + separator line + body lines.
  if (startIdx + 1 >= lines.length) return null;
  const headerRaw = lines[startIdx];
  const sepRaw = lines[startIdx + 1];
  if (!TABLE_SEP.test(sepRaw)) return null;
  if (!headerRaw.includes('|')) return null;
  const splitCells = (raw) =>
    raw.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(s => s.trim());
  const headers = splitCells(headerRaw);
  const body = [];
  let i = startIdx + 2;
  while (i < lines.length && lines[i].includes('|')) {
    body.push(splitCells(lines[i]));
    i += 1;
  }

  // Column alignment: honour the markdown markers (:--- / ---: / :---:)
  // when the model supplies them, and otherwise infer it — a column
  // whose every value is a figure is right-aligned so the digits stack.
  const markers = splitCells(sepRaw);
  const aligns = headers.map((_h, col) => {
    const marker = markers[col] || '';
    const left = marker.startsWith(':');
    const right = marker.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    if (left) return 'left';
    const values = body.map(row => row[col]).filter(v => v != null && v !== '');
    return values.length && values.every(looksNumeric) ? 'right' : 'left';
  });

  return { headers, body, aligns, consumed: i - startIdx };
}

export function renderMarkdown(md, brandIndex = []) {
  if (!md) return null;
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const nodes = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (FENCE.test(line)) {
      const buf = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i])) {
        buf.push(lines[i]);
        i += 1;
      }
      i += 1; // skip closing fence
      nodes.push(
        <pre key={`fence-${key++}`} className="chat-code-block">
          <code>{buf.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    const h = line.match(HEADING);
    if (h) {
      const level = h[1].length;
      const Tag = `h${Math.min(level + 2, 6)}`;
      nodes.push(
        React.createElement(
          Tag,
          { key: `h-${key++}`, className: 'chat-heading' },
          renderInline(h[2], brandIndex, `h${key}`),
        ),
      );
      i += 1;
      continue;
    }

    const table = parseTable(lines, i);
    if (table) {
      const tKey = key++;
      nodes.push(
        // The scroll container is not optional: a six-column table in a
        // 380px panel has to scroll inside its own box, or it drags the
        // whole conversation sideways.
        <div key={`t-${tKey}`} className="chat-table-wrap">
          <table className="chat-table">
            <thead>
              <tr>
                {table.headers.map((cell, idx) => (
                  <th key={idx} style={{ textAlign: table.aligns[idx] }}>
                    {renderInline(cell, brandIndex, `th${tKey}-${idx}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.body.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td
                      key={cIdx}
                      style={{ textAlign: table.aligns[cIdx] }}
                      className={table.aligns[cIdx] === 'right' ? 'chat-table-num' : undefined}
                    >
                      {renderInline(cell, brandIndex, `td${tKey}-${rIdx}-${cIdx}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      i += table.consumed;
      continue;
    }

    if (BULLET.test(line) || NUMBERED.test(line)) {
      const ordered = NUMBERED.test(line);
      const items = [];
      while (i < lines.length && (BULLET.test(lines[i]) || NUMBERED.test(lines[i]))) {
        const m2 = lines[i].match(ordered ? NUMBERED : BULLET);
        const content = ordered ? m2[2] : m2[3];
        items.push(content);
        i += 1;
      }
      const Tag = ordered ? 'ol' : 'ul';
      const lKey = key++;
      nodes.push(
        React.createElement(
          Tag,
          { key: `l-${lKey}`, className: 'chat-list' },
          items.map((it, idx) =>
            React.createElement(
              'li',
              { key: idx },
              renderInline(it, brandIndex, `li${lKey}-${idx}`),
            ),
          ),
        ),
      );
      continue;
    }

    if (line.trim() === '') {
      i += 1;
      continue;
    }

    // Paragraph: collect contiguous non-empty lines.
    const para = [line];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !HEADING.test(lines[i]) &&
      !FENCE.test(lines[i]) &&
      !BULLET.test(lines[i]) &&
      !NUMBERED.test(lines[i])
    ) {
      // Stop if a table starts on the next line.
      if (
        i + 1 < lines.length &&
        TABLE_SEP.test(lines[i + 1]) &&
        lines[i].includes('|')
      ) break;
      para.push(lines[i]);
      i += 1;
    }
    const pKey = key++;
    nodes.push(
      <p key={`p-${pKey}`} className="chat-paragraph">
        {renderInline(para.join(' '), brandIndex, `p${pKey}`)}
      </p>,
    );
  }

  return nodes;
}
